import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["type"],
  _excluded2 = ["_onTestContextReady"];
import PropTypes from 'prop-types';
import { PureComponent, createRef } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { ERROR_EMBED_CONTEXT_INVALID_RECEIVE_MESSAGE, ERROR_INVALID_CALLBACK_PROVIDED, createEmbeddedContext, createReadyMessage, createSyncAckMessage, isEmbeddedPropsChangedMessage, isMalformedMessageEvent, isMessageToMe, isSyncMessage, validFormat, validMessage } from '../messaging';
import { getEmbeddedPropsFromQueryParams, getTopLevelEmbeddedContextDefaults, isTopLevelWindowSupported } from '../utils/Embedded';
import { logEmbedError, logEmbedMessageReceived, logEmbedMessageSent } from '../utils/Logger';

/**
 * Additional props offered by the component returned by the wrapper
 */

/**
 * Type of the state of the component returned by the wrapper
 */

/**
 * Required props that the wrapped component should accept and handle
 */
import { jsx as _jsx } from "react/jsx-runtime";
// To allow your EmbeddedComponent to render outside of an iframe:
// set `enableTopLevelEmbed` to render in ALL environments including prod (ensure this will not have adverse consequences!)
// set `enableTopLevelEmbedForTestOnly` to render ONLY in the QA environment
const UIEmbeddedComponentHOC = (EmbeddedComponent, {
  enableTopLevelEmbed = false,
  enableTopLevelEmbedForTestOnly = false
} = {}) => {
  class UIEmbeddedComponent extends PureComponent {
    constructor(...args) {
      super(...args);
      this.callbacks = {};
      this.parent = {
        origin: null
      };
      this.child = {
        embeddedContext: null,
        receiveMessage: emptyFunction
      };
      this.rootRef = /*#__PURE__*/createRef();
      this.currentWindow = window;
      this.state = {
        embeddedProps: {},
        synced: false
      };
      this.createEmbeddedContext = ({
        embedId,
        group,
        name,
        appData
      }, sendMessageFn = this.sendMessage) => {
        return createEmbeddedContext({
          embedId,
          group,
          name,
          app: appData,
          sendMessageFn
        });
      };
      this.initEmbeddedContext = message => {
        this.child.embeddedContext = this.createEmbeddedContext(message.payload);
      };
      this.initWhenTopLevel = () => {
        this.child.embeddedContext = this.createEmbeddedContext(getTopLevelEmbeddedContextDefaults(), this.sendMessage);
        this.setState({
          synced: true,
          embeddedProps: getEmbeddedPropsFromQueryParams()
        });
      };
      this.validMessage = event => {
        if (isMalformedMessageEvent(event)) {
          return false;
        }
        if (!this.parent.origin) {
          if (validFormat(event.data)) {
            this.parent.origin = event.origin;
          }
        }
        return validMessage(event, this.parent.origin);
      };
      this.isToEmbeddedComponent = message => {
        return isMessageToMe(message, this.child.embeddedContext.id, this.child.embeddedContext.group);
      };
      this.hasCallback = message => {
        return !!this.callbacks[message.id];
      };
      this.validCallback = callback => {
        if (typeof callback === 'function') {
          return true;
        }
        logEmbedError(this.child.embeddedContext.name, ERROR_INVALID_CALLBACK_PROVIDED, JSON.stringify(callback));
        return false;
      };
      this.dispatchCallback = message => {
        this.callbacks[message.id](message.payload);
        delete this.callbacks[message.id];
      };
      this.sendMessage = (message, callback) => {
        var _this$currentWindow;
        if (callback && this.validCallback(callback)) {
          this.callbacks[message.id] = callback;
        }
        if (!((_this$currentWindow = this.currentWindow) !== null && _this$currentWindow !== void 0 && _this$currentWindow.parent)) {
          return;
        }
        this.currentWindow.parent.postMessage(message, this.parent.origin);
        logEmbedMessageSent(this.child.embeddedContext.name, message);
      };
      this._simulatePostMessage = event => {
        this.handleReceiveMessage(event);
      };
      this.logMessageReceived = message => {
        if (!this.child.embeddedContext) {
          return;
        }
        logEmbedMessageReceived(this.child.embeddedContext.name, message);
      };
      this.handleOnReady = childContext => {
        if (typeof childContext.receiveMessage !== 'function') {
          logEmbedError(this.child.embeddedContext.name, ERROR_EMBED_CONTEXT_INVALID_RECEIVE_MESSAGE, childContext);
          return;
        }
        this.child.receiveMessage = childContext.receiveMessage;
        const readyMessage = createReadyMessage(this.child.embeddedContext.id, childContext.data);
        this.sendMessage(readyMessage);
      };
      this.handleSyncMessage = message => {
        if (this.state.synced) {
          return;
        }
        this.sendMessage(createSyncAckMessage(message));
        this.setState({
          synced: true,
          embeddedProps: message.payload.embeddedProps
        });
      };
      this.handleEmbeddedPropsChangedMessage = message => {
        const _message$payload = message.payload,
          embeddedProps = _objectWithoutPropertiesLoose(_message$payload, _excluded);
        this.setState({
          embeddedProps
        });
      };
      this.handleReceiveMessage = event => {
        if (!this.validMessage(event)) {
          return;
        }
        const {
          data: message
        } = event;
        if (isSyncMessage(message) && !this.state.synced) {
          this.initEmbeddedContext(message);
        }
        this.logMessageReceived(message);
        if (isSyncMessage(message)) {
          this.handleSyncMessage(message);
        } else if (isEmbeddedPropsChangedMessage(message)) {
          this.handleEmbeddedPropsChangedMessage(message);
        } else if (this.state.synced) {
          if (this.hasCallback(message)) {
            this.dispatchCallback(message);
          } else if (this.isToEmbeddedComponent(message)) {
            this.child.receiveMessage(message);
          }
        }
      };
      this.setCurrentWindow = source => {
        if (!source) {
          return;
        }
        this.currentWindow = source.ownerDocument.defaultView;
      };
    }
    componentDidMount() {
      this.props._onTestContextReady({
        simulatePostMessage: this._simulatePostMessage
      });
      if (this.currentWindow) {
        this.currentWindow.addEventListener('message', this.handleReceiveMessage, false);
      }
      if (isTopLevelWindowSupported({
        enableTopLevelEmbed,
        enableTopLevelEmbedForTestOnly
      })) {
        this.initWhenTopLevel();
      }
    }
    componentWillUnmount() {
      if (!this.currentWindow) {
        return;
      }
      this.currentWindow.removeEventListener('message', this.handleReceiveMessage);
    }

    // used for testing only

    render() {
      if (!this.state.synced) {
        return /*#__PURE__*/_jsx("div", {
          ref: this.setCurrentWindow
        });
      }
      const _this$props = this.props,
        rest = _objectWithoutPropertiesLoose(_this$props, _excluded2);
      return /*#__PURE__*/_jsx(EmbeddedComponent, Object.assign({}, rest, this.state.embeddedProps, {
        onReady: this.handleOnReady,
        embeddedContext: this.child.embeddedContext
      }));
    }
  }
  UIEmbeddedComponent.propTypes = {
    _onTestContextReady: PropTypes.func.isRequired
  };
  UIEmbeddedComponent.defaultProps = {
    _onTestContextReady: emptyFunction
  };
  UIEmbeddedComponent.EmbeddedComponent = EmbeddedComponent;
  return UIEmbeddedComponent;
};
export default UIEmbeddedComponentHOC;